import { useEffect, useState } from "react";
// import GlobalStyle from "./global-style";
import "react-loading-skeleton/dist/skeleton.css";
import "./index.css";
import { router } from "./router/index";
import { gql } from "./__generated__";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  useMutation,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { v4 as uuidv4 } from "uuid";
import { postMessage } from "./core/message";
import "react-toastify/dist/ReactToastify.css";
import { RouterProvider } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";
import { usePullToRefresh } from "./core/pull-to-refresh-provider";
import { isAndroidApp, isApp, isIOSApp } from "./utils/user-agent";
import { ReportModal } from "./components/common/report-modal";
import { notifyError } from "./utils/toast";
import { Report } from "./__generated__/graphql";
import packageJson from "../package.json";
import { useAuth } from "./core/auth";

// 신고 기능이 몇 달 쨰 작동하지 않음. 불필요한 쿼리로 판단. 일단 주석처리. (9월24일 by 승원)
// const ME = gql(`
//   query me2 {
//     me {
//       pendingReport {
//         id
//         store {
//           title
//         }
//         reason
//         result
//         isDeleted
//         createdAt
//       }
//     }
//   }
// `);

const RESOLVE_REPORT = gql(`
  mutation resolveReport($reportId: Int!) {
    resolveReport(reportId: $reportId) {
      ok
      error
    }
  }
`);

// app일 경우 푸시토큰을 웹 저장소에 저장.
window.addEventListener("message", (event) => {
  if (
    typeof event.data === "string" &&
    event.data.startsWith("ExponentPushToken")
  ) {
    const pushToken = event.data;
    localStorage.setItem("expo_push_token", pushToken);

    const jwt = localStorage.getItem("jwt");
    const uuid = localStorage.getItem("mahi_uuid");
    const sellerToken = localStorage.getItem("sellerToken");
    if (sellerToken) {
      fetch(`${process.env.REACT_APP_API_URL}/seller/push-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "seller-authorization": `Bearer ${sellerToken}`,
        },
        body: JSON.stringify({
          push_token: pushToken,
        }),
      }).catch((error) => {});
      return;
    } else if (jwt) {
      fetch(`${process.env.REACT_APP_API_URL}/user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          push_token: pushToken,
        }),
      }).catch((error) => {});
    } else if (uuid) {
      fetch(`${process.env.REACT_APP_API_URL}/guest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: uuid,
          push_token: pushToken,
        }),
      }).catch((error) => {});
    }
  }
});

const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_API_URL,
});

const authLink = setContext((_, { headers }) => {
  // 유저 토큰
  const userToken = localStorage.getItem("jwt");

  // 셀러 토큰
  const sellerToken = localStorage.getItem("sellerToken");

  // 관리자 토큰
  const adminToken = localStorage.getItem("adminToken");

  // 매니저 토큰
  const managerToken = localStorage.getItem("managerToken");

  // 브랜드 토큰
  const brandToken = localStorage.getItem("brandToken");

  const uuid = isApp() ? localStorage.getItem("mahi_uuid") : null;

  const pushToken = isApp() ? localStorage.getItem("expo_push_token") : null;

  const os = isAndroidApp() ? "android" : isIOSApp() ? "ios" : "web";

  // 헤더에 유저 및 셀러 토큰을 추가
  return {
    headers: {
      ...headers,
      authorization: userToken ? `Bearer ${userToken}` : "",
      "seller-authorization": sellerToken ? `Bearer ${sellerToken}` : "", // 셀러용 헤더
      "admin-authorization": adminToken ? `Bearer ${adminToken}` : "", // 관리자용 헤더
      "manager-authorization": managerToken ? `Bearer ${managerToken}` : "", // 매니저용 헤더
      "brand-authorization": brandToken ? `Bearer ${brandToken}` : "", // 브랜드용 헤더
      "Apollo-Require-Preflight": "true",
      ...(uuid ? { uuid } : {}), // uuid가 있을 때만 추가
      ...(pushToken ? { "push-token": pushToken } : {}),
      ...(os ? { os } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      if (err.extensions?.code === "UNAUTHENTICATED") {
        // 유저 토큰 만료 시 유저 토큰 삭제
        if (localStorage.getItem("jwt")) {
          localStorage.removeItem("jwt");
          window.location.reload();
          return;
        }

        // 셀러 토큰 만료 시 셀러 토큰 삭제
        if (localStorage.getItem("sellerToken")) {
          localStorage.removeItem("sellerToken");
          window.location.reload();
          return;
        }

        // 관리자 토큰 만료 시 관리자 토큰 삭제
        if (localStorage.getItem("adminToken")) {
          localStorage.removeItem("adminToken");
          window.location.reload();
          return;
        }

        // 브랜드 토큰 만료 시 브랜드 토큰 삭제
        if (localStorage.getItem("brandToken")) {
          localStorage.removeItem("brandToken");
          window.location.reload();
          return;
        }
      }
    }
  }
});

export const client = new ApolloClient({
  link: ApolloLink.from([
    authLink, // 1. 토큰
    errorLink, // 2. 에러
    uploadLink, // 3. 업로드
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Campaign: {
        fields: {
          runs: {
            // runs 배열은 항상 새로운 데이터로 교체 (병합하지 않음)
            merge: false,
          },
        },
      },
    },
  }),
});

const CURRENT_VERSION = packageJson.version; // 현재 빌드 버전

function App() {
  const [isOutdated, setIsOutdated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReportModalOpen, setReportModalOpen] = useState(false);
  const [report] = useState<Report | null>(null);
  const { isPullable } = usePullToRefresh();
  const { isFirst } = useAuth();

  /*
   * 유저에게 접수된 문의 목록 조회
   */
  // useQuery(ME, {
  //   fetchPolicy: "network-only",
  //   skip: !localStorage.getItem("jwt"), // 유저 토큰이 있을 경우에만 쿼리 실행
  //   onCompleted: (data) => {
  //     if (data.me?.pendingReport) {
  //       setReportModalOpen(true);
  //       setReport(data.me?.pendingReport as Report);
  //     }
  //   },
  // });

  /*
   * 유저에게 접수된 문의 해결 처리
   */
  const [resolveReport] = useMutation(RESOLVE_REPORT, {
    onCompleted: (data) => {
      if (data.resolveReport.ok) {
        setReportModalOpen(false);
      }
    },
  });

  const initUuid = () => {
    if (!localStorage.getItem("mahi_uuid")) {
      localStorage.setItem("mahi_uuid", uuidv4());
    }
  };

  const initPushToken = () => {
    if (!isFirst && !localStorage.getItem("expo_push_token")) {
      postMessage("REQ_NOTIFICATION_TOKEN", ""); // 알림 권한 요청하라고 앱에게 메세지 보내기
    }
  };

  useEffect(() => {
    initUuid();
    initPushToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setIsLoading(true);

    // pull-to-refresh 시 session storage 초기화
    const currentPath = window.location.pathname;

    if (currentPath === "/") {
      // 홈 페이지 관련 데이터
      sessionStorage.removeItem("home:allStores");
      sessionStorage.removeItem("home:offset");
      sessionStorage.removeItem("home:hasMore");
      sessionStorage.removeItem("home:locationKey");
      sessionStorage.removeItem("scroll-home");
      sessionStorage.removeItem("viewType");
      sessionStorage.removeItem("home-download-sheet-shown");
    } else if (currentPath === "/explore") {
      // explore 페이지 관련 데이터
      sessionStorage.removeItem("explore:allReviews");
      sessionStorage.removeItem("explore:offset");
      sessionStorage.removeItem("explore:hasMore");
      sessionStorage.removeItem("explore:selected_filter");
    } else if (currentPath === "/like") {
      // like 페이지 관련 데이터
      sessionStorage.removeItem("like:allStores");
      sessionStorage.removeItem("like:offset");
      sessionStorage.removeItem("like:hasMore");
      sessionStorage.removeItem("like:locationKey");
    }

    await new Promise((resolve) => setTimeout(resolve, 350));
    window.location.reload();
    return true;
  };

  useEffect(() => {
    const checkVersion = async () => {
      const res = await fetch("/version.json", { cache: "no-cache" });
      const data = await res.json();
      if (data.version !== CURRENT_VERSION && data.needUpdate === true) {
        setIsOutdated(true);
      }
    };

    checkVersion();

    // 예시: 30초 마다 다시 확인
    const interval = setInterval(checkVersion, 30 * 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <>
      <div
        style={{
          opacity: isLoading ? 0.5 : 1,
          transition: "opacity 0.3s ease",
        }}
      >
        <PullToRefresh
          isPullable={isPullable}
          onRefresh={handleRefresh}
          pullingContent={<></>}
          pullDownThreshold={95}
        >
          <>
            {isOutdated && (
              <div className="fixed bottom-[64px] w-full max-w-[742px] mx-auto bg-[#1692fc] text-white flex items-center justify-center text-center py-2 z-50 h-[64px]">
                앱을 업데이트 했어요! 새로고침 후 이용해주세요.
                <button
                  onClick={() => window.location.reload()}
                  className="ml-2 underline"
                >
                  새로고침
                </button>{" "}
              </div>
            )}
            <RouterProvider router={router} />
          </>
        </PullToRefresh>
        {isReportModalOpen && report && (
          <ReportModal
            report={report}
            onClose={() => {
              resolveReport({
                variables: { reportId: report.id },
                onCompleted: (data) => {
                  if (data.resolveReport.ok) {
                    setReportModalOpen(false);
                  } else {
                    notifyError(
                      data.resolveReport.error ||
                        "알 수 없는 오류가 발생했습니다."
                    );
                  }
                },
                onError: (error) => {
                  notifyError(error.message);
                },
              });
            }}
          />
        )}
      </div>
    </>
  );
}

export default App;
